// Shared reference data for the Feedback module (Dashboard + Browse Feedback).
//
// Purchase channels / languages / rating dimensions are the feedback app's
// own frontend-owned static allowlists (src/config/constants.ts in
// MF-Customer-Feedback-App-Api) — safe to mirror here since that repo's docs
// say those never move.
//
// Kiosk locations (stores) are NOT mirrored here — they're real records
// (UUID ids) managed by admins on the feedback side, so the list is loaded
// live via getFeedbackLocationsAction and passed into these screens; it also
// resolves each feedback row's storeLocationId to a display name, since rows
// only carry the raw id.
//
// There is no Supervisor filter/column — the feedback service's
// service-token contract (used for this server-to-server integration) has no
// submittedByUserId filter and list items carry no supervisor name.

export const PURCHASE_CHANNELS = [
  { id: "store", name: "Store" },
  { id: "online", name: "Online" },
  { id: "exhibition", name: "Exhibition" },
];

export const LANGUAGES: Record<string, string> = {
  en: "English",
  ta: "Tamil",
  te: "Telugu",
  ml: "Malayalam",
  kn: "Kannada",
  hi: "Hindi",
};

export const channelLabel = (id: string) => PURCHASE_CHANNELS.find((c) => c.id === id)?.name || id || "—";
export const languageLabel = (code: string) => LANGUAGES[code] || code || "—";

// Pill colors matching the feedback app's own Admin Panel (Online = purple,
// Store = pink/rose). Exhibition isn't shown in that reference — amber is
// the third pastel in the same family.
export const channelBadgeClass = (id: string) => {
  switch (id) {
    case "online":
      return "bg-purple-100 text-purple-700 hover:bg-purple-100";
    case "store":
      return "bg-rose-100 text-rose-700 hover:bg-rose-100";
    case "exhibition":
      return "bg-amber-100 text-amber-700 hover:bg-amber-100";
    default:
      return "bg-muted text-muted-foreground hover:bg-muted";
  }
};

// The 8 rating dimensions the feedback service returns under item.ratings,
// short-labeled to fit the dashboard's bar chart x-axis.
export const RATING_DIMENSIONS: { key: string; label: string; fullLabel: string }[] = [
  { key: "productQuality", label: "Quality", fullLabel: "Product Quality" },
  { key: "productVariety", label: "Variety", fullLabel: "Product Variety" },
  { key: "pricing", label: "Price", fullLabel: "Pricing" },
  { key: "staffBehaviour", label: "Staff", fullLabel: "Staff Behaviour" },
  { key: "storeExperience", label: "Store", fullLabel: "Store Experience" },
  { key: "deliveryExperience", label: "Deliv", fullLabel: "Delivery Experience" },
  { key: "recommend", label: "Rec", fullLabel: "Would Recommend" },
  { key: "overallExperience", label: "Overall", fullLabel: "Overall Experience" },
];

// Shape returned per item by GET /kiosk-locations (proxied via
// getFeedbackLocationsAction) — used to populate the Store filter dropdown
// and to look up a display name for each feedback row's storeLocationId.
export interface FeedbackKioskLocation {
  id: string;
  name: string;
  locationName?: string | null;
  locationType: "STORE" | "EXHIBITION";
  isActive: boolean;
}

// Single dropdown label for a store/location, shared by the Feedback and
// Rental filters so both show the same text for the same record.
export const locationLabel = (l: { name?: string | null; locationName?: string | null }) =>
  [l.name, l.locationName].filter(Boolean).join(" - ");
